/**
 * ForYouPage — Personalized content landing page.
 * Two modes:
 *   1. Has preferences → shows personalized article feed + edit controls
 *   2. No preferences → shows full ForYouPreferencePicker
 * 
 * Route: /for-you
 *
 * SSR note: App.mjs provides only Redux Provider (no Router context),
 * so we use <a> tags for auth links and guard client-only content with useIsMounted.
 *
 * Client-side nav note: When navigating here via React Router (not a full-page
 * load), preloadedState won't have forYou data. We must dispatch
 * fetchForYouPreferences() to populate the slice on mount.
 */
import React, { lazy, Suspense, useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setPageTitle, setPageDescription, setCanonicalUrl } from '../slices/pageMetadataSlice.mjs';
import { setNavigationItems } from '../slices/headerSlice.mjs';
import {
    fetchForYouPreferences, updateForYouPreferences, clearForYouPreferences,
    initDraft, clearDraft,
} from '../slices/forYouSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';

// Lazy-load heavy components — only loaded client-side
const ForYouPreferencePicker = lazy(() => import('../components/forYou/ForYouPreferencePicker.mjs'));
const ForYouFeed = lazy(() => import('../components/forYou/ForYouFeed.mjs'));

// Stable fallback references for when forYou slice isn't hydrated (client-side
// nav from a page whose SSR store didn't include this reducer).
const EMPTY_FOR_YOU = Object.freeze({});
const EMPTY_DRAFT = Object.freeze({ categories: [], topics: [] });

export default function ForYouPage() {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const forYou = useSelector((state) => state.forYou ?? EMPTY_FOR_YOU);
    const { followedCategories, followedTopics, hasPreferences, loading } = forYou;
    const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
    const navigationItems = useSelector((state) => state.headerSlice?.navigationItems);

    const [showPicker, setShowPicker] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fetchedRef = useRef(false);
    const fabRef = useRef(null);
    const draft = useSelector((state) => state.forYou?.draft ?? EMPTY_DRAFT);

    // Set page metadata
    useEffect(() => {
        dispatch(setPageTitle('For You - Nexus of Truth'));
        dispatch(setPageDescription('Your personalized content feed'));
        if (typeof window !== 'undefined') {
            dispatch(setCanonicalUrl(`${window.location.origin}/for-you`));
        }
    }, [dispatch]);

    // Scroll listener — toggle FAB position on desktop via classList (zero re-renders)
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(min-width: 640px)');
        if (!mq.matches) return; // mobile — no scroll animation

        const THRESHOLD = 80;
        let scrolled = false;
        const onScroll = () => {
            const shouldBeScrolled = window.scrollY > THRESHOLD;
            if (shouldBeScrolled !== scrolled) {
                scrolled = shouldBeScrolled;
                fabRef.current?.classList.toggle('fy-page__morph-fab--scrolled', scrolled);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // ─── Client-side navigation data loading ───
    // On SSR, preloadedState populates the forYou slice. But on client-side
    // navigation (React Router), the slice starts with the default empty state.
    // Dispatch fetchForYouPreferences to load the user's selections.
    useEffect(() => {
        if (!isMounted) return;
        if (!isAuthenticated) return;
        if (fetchedRef.current) return;
        // If currently loading (unlikely but guard), skip
        if (loading) return;

        fetchedRef.current = true;
        dispatch(fetchForYouPreferences());
    }, [isMounted, isAuthenticated, loading, dispatch]);

    /** Open picker and initialize draft from current saved preferences */
    const handleOpenPicker = useCallback(() => {
        dispatch(initDraft());
        setShowPicker(true);
    }, [dispatch]);

    /** FAB click: opens picker or saves draft selections */
    const handleFabClick = useCallback(async () => {
        if (!showPicker) {
            handleOpenPicker();
            return;
        }

        // Dirty check — skip API call if nothing changed
        const draftCats = new Set(draft.categories || []);
        const draftTopics = new Set(draft.topics || []);
        const savedCats = new Set(followedCategories || []);
        const savedTopics = new Set(followedTopics || []);

        // User cleared all selections — delete prefs from server
        // This resets hasPreferences=false, showing the fresh picker view
        if (draftCats.size === 0 && draftTopics.size === 0) {
            setIsSaving(true);
            try {
                await dispatch(clearForYouPreferences()).unwrap();
                dispatch(clearDraft());
                setShowPicker(false);
                // Also strip ForYou children from header nav so it updates immediately
                if (navigationItems) {
                    const updated = navigationItems.map(item =>
                        item.isForYou ? { ...item, children: [] } : item
                    );
                    dispatch(setNavigationItems(updated));
                }
            } catch { /* handled by slice */ }
            setIsSaving(false);
            return;
        }

        const unchanged =
            draftCats.size === savedCats.size &&
            draftTopics.size === savedTopics.size &&
            [...draftCats].every(c => savedCats.has(c)) &&
            [...draftTopics].every(t => savedTopics.has(t));

        if (unchanged) {
            dispatch(clearDraft());
            setShowPicker(false);
            return;
        }

        // Save draft selections to server via thunk
        setIsSaving(true);
        try {
            await dispatch(updateForYouPreferences({
                followedCategories: [...draftCats],
                followedTopics: [...draftTopics],
            })).unwrap();
            dispatch(clearDraft());
            setShowPicker(false);
        } catch { /* error shown via slice error state */ }
        setIsSaving(false);
    }, [showPicker, handleOpenPicker, dispatch, draft.categories, draft.topics, followedCategories, followedTopics, navigationItems]);

    // ─── SSR-safe shell: show minimal content until client mounts ───
    if (!isMounted) {
        return (
            <div className="fy-page">
                <div className="fy-page__empty">
                    <div className="fy-page__icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    </div>
                    <h1 className="fy-page__heading">For You</h1>
                    <p className="fy-page__text">Loading your personalized content…</p>
                </div>
            </div>
        );
    }

    // ─── Loading: show spinner while fetching preferences ───
    if (loading) {
        return (
            <div className="fy-page">
                <div className="fy-page__empty">
                    <div className="fy-page__spinner" />
                    <h1 className="fy-page__heading">For You</h1>
                    <p className="fy-page__text">Loading your preferences…</p>
                </div>
            </div>
        );
    }


    // ─── Not authenticated ───
    if (!isAuthenticated) {
        return (
            <div className="fy-page">
                <div className="fy-page__unauthenticated">
                    <div className="fy-page__icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    </div>
                    <h1 className="fy-page__heading">Personalized For You</h1>
                    <p className="fy-page__text">
                        Sign up or log in to pick your favorite categories and topics.
                        We'll curate a personalized navigation just for you.
                    </p>
                    <div className="fy-page__auth-buttons">
                        <Link to="/signup?returnTo=%2Ffor-you" className="fy-page__btn fy-page__btn--primary">Sign Up</Link>
                        <Link to="/login?returnTo=%2Ffor-you" className="fy-page__btn fy-page__btn--secondary">Log In</Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─── No preferences yet — show picker directly with FAB ───
    if (!hasPreferences) {
        return (
            <div className="fy-page">
                <Suspense fallback={<div className="fy-page__empty"><p>Loading…</p></div>}>
                    <ForYouPreferencePicker />
                </Suspense>

                {/* FAB — Save Interests (always visible in first-time picker) */}
                <button
                    ref={fabRef}
                    className="fy-page__morph-fab fy-page__morph-fab--active"
                    onClick={async () => {
                        setIsSaving(true);
                        try {
                            await dispatch(updateForYouPreferences({
                                followedCategories: draft.categories || [],
                                followedTopics: draft.topics || [],
                            })).unwrap();
                            dispatch(clearDraft());
                        } catch { /* handled by slice */ }
                        setIsSaving(false);
                    }}
                    disabled={isSaving}
                    type="button"
                >
                    <span className="fy-page__morph-fab-icon">
                        {isSaving ? (
                            <span className="fy-page__morph-fab-spinner" />
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        )}
                    </span>
                    <span className="fy-page__morph-fab-label">
                        {isSaving ? 'Saving…' : 'Save Interests'}
                    </span>
                </button>
            </div>
        );
    }

    // ─── Has preferences — show personalized dashboard with content feed ───
    const catCount = followedCategories?.length || 0;
    const topicCount = followedTopics?.length || 0;

    // Build smart subheading — only mention what has selections, suggest the rest
    const subheadingParts = [];
    if (catCount > 0) subheadingParts.push(`${catCount} ${catCount === 1 ? 'category' : 'categories'}`);
    if (topicCount > 0) subheadingParts.push(`${topicCount} ${topicCount === 1 ? 'topic' : 'topics'}`);
    const followingSummary = `Following ${subheadingParts.join(' and ')}`;

    let suggestion = '';
    if (catCount === 0 && topicCount > 0) suggestion = ' · Add categories to broaden your feed';
    else if (topicCount === 0 && catCount > 0) suggestion = ' · Add topics to refine your feed';

    return (
        <div className="fy-page">
            <div className="fy-page__dashboard">
                {/* Dashboard header */}
                <div className="fy-page__dashboard-header">
                    <div>
                        <h1 className="fy-page__heading">For You</h1>
                        <p className="fy-page__subheading">
                            {followingSummary}
                            {suggestion && <span className="fy-page__suggestion">{suggestion}</span>}
                        </p>
                    </div>
                </div>

                {/* Inline picker — slides in below header */}
                <div className={`fy-page__inline-picker ${showPicker ? 'fy-page__inline-picker--open' : ''}`}>
                    {showPicker && (
                        <Suspense fallback={<div className="fy-page__empty"><p>Loading…</p></div>}>
                            <ForYouPreferencePicker compact className="fy-page__embedded-picker" />
                        </Suspense>
                    )}
                </div>

                {/* Content feed */}
                <Suspense fallback={
                    <div className="fy-feed__loading">
                        <div className="fy-page__spinner" />
                        <p>Loading your feed…</p>
                    </div>
                }>
                    <ForYouFeed />
                </Suspense>
            </div>

            {/* FAB — Edit Interests ↔ Save Interests, fixed position */}
            <button
                ref={fabRef}
                className={`fy-page__morph-fab ${showPicker ? 'fy-page__morph-fab--active' : ''}`}
                onClick={handleFabClick}
                disabled={isSaving}
                type="button"
            >
                <span className="fy-page__morph-fab-icon">
                    {isSaving ? (
                        <span className="fy-page__morph-fab-spinner" />
                    ) : showPicker ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                            <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                        </svg>
                    )}
                </span>
                <span className="fy-page__morph-fab-label">
                    {isSaving ? 'Saving…' : showPicker ? 'Save Interests' : 'Edit Interests'}
                </span>
            </button>
        </div>
    );
}
